import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCarePlanInstance } from "../../../redux/actions/careplanActions";
import {
  overviewService,
  checkLockStatus,
} from "../../../services/dashboardServices";
import { fetchOverviewDraftCarePlans } from "../../../redux/actions/careplanActions";
import { formatDate } from "../../../utils/dateUtils";
import CarePlanEngagementChart from "../CarePlanEngagementChart";
import ViewCarePlan from "../CarePlan/ViewCarePlan";
import EditCarePlan from "../CarePlan/EditCarePlan";
import CarePlanMDTUsers from "../CarePlan/CarePlanMDTUsers";
import DraftPlanView from "../CarePlan/DraftPlanView";
import CarePlanAuditLogs from "../AuditLog/CarePlanAuditLogs";
import FullScreenOverlay from "../../common/FullScreenOverlay";

import { InfoFilled } from "../../common/Icons";
import Avatar from "../../common/Avatar";
import Menu from "../../common/Menu";
import TextField from "../../common/TextField";
import { getDraftPlanMenu } from "../../../utils/draftCarePlanMenu";
import { useCarePlanActions } from "../../../hooks/useCarePlanActions";

import "./Overview.css";

const Overview = () => {
  const dispatch = useDispatch();

  const overviewDrafts = useSelector(
    (state) => state.careplan.overviewDraftCarePlans
  );
  const overviewDraftsLoading = useSelector(
    (state) => state.careplan.overviewDraftsLoading
  );

  const { userProfile, isAdmin, selected, modals, handlers } =
    useCarePlanActions();

  const [activePlans, setActivePlans] = useState(0);
  const [draftPlans, setDraftPlans] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalCarePlanViews, setTotalCarePlanViews] = useState(0);
  // For the chart
  const [chartLabels, setChartLabels] = useState([]);
  const [chartValues, setChartValues] = useState([]);
  // For the draft care plans
  const [deleteText, setDeleteText] = useState("");
  const [planLockedBy, setPlanLockedBy] = useState(null);
  const [showCarePlanLockedModal, setShowCarePlanLockedModal] = useState(false);
  const [showPublishValidationError, setShowPublishValidationError] =
    useState(false);
  const [isImportantInfoEmpty, setIsImportantInfoEmpty] = useState(false);

  useEffect(() => {
    overviewService()
      .then((data) => {
        setActivePlans(data.active_plans_count);
        setDraftPlans(data.draft_plans_count);
        setTotalUsers(data.total_users_count);
        setTotalCarePlanViews(data.total_careplan_views);
        // For the chart
        if (data.engagement_data) {
          setChartLabels(data.engagement_data.labels);
          setChartValues(data.engagement_data.values);
        }
      })
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    dispatch(fetchOverviewDraftCarePlans());
  }, [dispatch]);

  const handleEditClick = async (plan) => {
    const status = await checkLockStatus(plan.id);

    if (status.is_locked) {
      // Update Redux store with lock status
      dispatch({
        type: "careplan/updateCarePlanLockStatus",
        payload: {
          id: plan.id,
          is_locked: true,
          locked_by: status.locked_by,
        },
      });
    }

    if (status.is_locked && status.locked_by.id !== userProfile.id) {
      setPlanLockedBy(status.locked_by.name);
      setShowCarePlanLockedModal(true);
      return;
    }
    // we can lock & open the modal now
    handlers.onEdit(plan);
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Delete") {
      deleteTextField.setCustomValidity(
        'Please type "Delete" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }

    setDeleteText(""); //reset the delete text otherwise the next time the text box show Delete
    handlers.onConfirmDelete();
  };

  const validateBeforePublish = async (plan) => {
    // load the full draft
    const { payload: instance } = await dispatch(
      fetchCarePlanInstance(plan.id)
    );

    // skip NHS-number section (id===1) and see if any answer is empty
    const hasEmpty = instance.sections.some((section) => {
      if (section.id === 1) return false;
      return section.items.some((item) => !item.answer?.trim());
    });

    if (hasEmpty) {
      setShowPublishValidationError(true);
      return;
    }

    // now check your important_info field
    if (!instance.important_info?.trim()) {
      setIsImportantInfoEmpty(true);
      setShowPublishValidationError(true);
      return;
    }

    // finally, go ahead
    handlers.onPublish(plan);
  };

  return (
    <div className="overview-container">
      <div className="stats-cards">
        <div className="stats-card">
          <div className="stats-card-text">Total Active Plans</div>
          <p className="stats-value h3-semi-bold">{activePlans}</p>
        </div>
        <div className="stats-card">
          <div className="stats-card-text">Total Draft Plans</div>
          <p className="stats-value h3-semi-bold">{draftPlans}</p>
        </div>
        <div className="stats-card">
          <div className="stats-card-text">Total Views</div>
          <p className="stats-value h3-semi-bold">{totalCarePlanViews}</p>
        </div>
        <div className="stats-card">
          <div className="stats-card-text">Total Users</div>
          <p className="stats-value h3-semi-bold">{totalUsers}</p>
        </div>
      </div>

      <div className="engagement-chart-wrapper">
        <div className="engagement-chart">
          <p>Care Plan Engagement</p>
          {chartLabels.length > 0 && chartValues.length > 0 && (
            <CarePlanEngagementChart
              labels={chartLabels}
              values={chartValues}
            />
          )}
        </div>
      </div>

      <div className="updated-draft-care-plans">
        <h3>Updated Draft Care Plans</h3>
        {overviewDraftsLoading ? (
          <div className="partial-loader">
            <p>Loading draft care plans...</p>
            {/* or a nice spinner */}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="care-plan-table">
              <thead>
                <tr>
                  <th>NHS number</th>
                  <th className="hide-on-mobile">Created by</th>
                  <th>Last updated</th>
                  <th>Users</th>
                  <th className="hide-on-mobile"></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {overviewDrafts.map((row) => (
                  <tr
                    key={row.id}
                    className={row.locked_by ? "locked-row" : ""}
                  >
                    <td>
                      <button
                        className="link-button"
                        onClick={() => handlers.onView(row)}
                      >
                        {row.nhsNumber}
                      </button>
                    </td>
                    <td className="hide-on-mobile">{row.created_by}</td>
                    <td>{formatDate(row.updated_at)}</td>
                    <td>
                      <div className="mdt-users">
                        {row.assigned_users?.map((user, idx) => (
                          <div
                            key={idx}
                            style={{
                              zIndex: row.assigned_users.length - idx,
                              marginLeft: idx === 0 ? 0 : -12,
                              position: "relative",
                            }}
                          >
                            <Avatar
                              key={idx}
                              profilePictureUrl={user.profile_picture}
                              name={user.name}
                              useDefault={true}
                              size={28}
                              bgColor="lightgray"
                            />
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="hide-on-mobile">
                      {row.locked_by && (
                        <div className="draftplan-locked">
                          Locked{" "}
                          <span className="draftplan-locked-icon">
                            <InfoFilled />
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="icon-cell">
                      <Menu
                        menuItems={getDraftPlanMenu(row, userProfile, isAdmin, {
                          ...handlers,
                          onEdit: handleEditClick, // <-- swap in your wrapper
                          onPublish: validateBeforePublish,
                        })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* {selectedCarePlanId && (
              <ViewCarePlan
                carePlanId={selectedCarePlanId}
                onClose={() => setSelectedCarePlanId(null)}
              />
            )} */}
            </table>
          </div>
        )}
      </div>
      {modals.view && selected && (
        <ViewCarePlan
          carePlanId={selected.id}
          version={selected.version}
          onClose={handlers.onCloseView}
        />
      )}
      {modals.edit && selected && (
        <EditCarePlan
          carePlanId={selected.id}
          nhsNumber={selected.nhsNumber}
          onClose={handlers.onCloseEdit}
        />
      )}
      {modals.users && selected && (
        <FullScreenOverlay
          title="Manage Plan Access"
          popupType="add-user"
          buttons={[]}
          iconClose={true}
          onClose={handlers.onCloseManageUsers}
        >
          <CarePlanMDTUsers selectedCarePlan={selected} />
        </FullScreenOverlay>
      )}
      {modals.publish && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: handlers.onClosePublish,
            },
            {
              label: selected.version > 1 ? "Yes, Replace" : "Yes, Publish",
              onClick: handlers.onConfirmPublish, // Handle delete confirmation
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Publish careplan?</h5>
          {selected.version > 1 ? (
            <p>Are you sure you want to replace plan?</p>
          ) : (
            <p>Are you sure you want to make this draft care plan active?</p>
          )}
        </FullScreenOverlay>
      )}

      {modals.delete && (
        <FullScreenOverlay
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setDeleteText("");
                handlers.onCloseDelete();
              },
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDelete, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm Care Plan deletion</h5>
          <p>
            Are you sure you want to delete the
            <span className="confirm-delete-nhs">
              {" "}
              Care Plan {selected.nhsNumber}
            </span>
            ? This action cannot be undone and all the data will be permanently
            removed.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value); // Update state
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}

      {showCarePlanLockedModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            { label: "OK", onClick: () => setShowCarePlanLockedModal(false) },
          ]}
        >
          <h5 className="semi-bold">Care Plan is locked</h5>
          <p>
            The care plan is currently being edited by <b>{planLockedBy}</b>.
            Please try again later.
          </p>
        </FullScreenOverlay>
      )}

      {modals.unlock && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: handlers.onCloseUnlock,
            },
            {
              label: "Yes, Unlock",
              onClick: handlers.onConfirmUnlock,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Unlock care plan?</h5>
          <p>
            Are you sure you want to unlock this care plan?
            <br />
            If someone is editing, their changes will be lost.
          </p>
        </FullScreenOverlay>
      )}
      {modals.export && selected && (
        <DraftPlanView
          carePlanId={selected.id}
          onClose={handlers.onCloseExport}
        />
      )}
      {modals.audit && selected && (
        <CarePlanAuditLogs
          carePlanId={selected.id}
          onClose={handlers.onCloseAuditLog}
        />
      )}
      {showPublishValidationError && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal
          isPopup
          buttons={[
            {
              label: "Close",
              onClick: () => {
                setShowPublishValidationError(false);
                setIsImportantInfoEmpty(false);
              },
            },
          ]}
        >
          <h5 className="semi-bold">Cannot publish care plan</h5>
          {isImportantInfoEmpty ? (
            <p>
              Please fill in “Important information if I need to go into
              hospital” before publishing.
            </p>
          ) : (
            <p>
              Please complete all required items before publishing this care
              plan.
            </p>
          )}
        </FullScreenOverlay>
      )}
    </div>
  );
};

export default Overview;
